import Phrase, { fetchPhrase } from '../../libs/translationsUtils/phrase'
import { Component, ComponentChild } from 'preact'
import TopicFavorite from '../topic-list-view/TopicFavorite'
import { common } from '../../preact-components/common'
import ReportContentModal from '@insided/destination/modules/Content/TopicView/Post/ReportContentModal'
import AlertComponent from '@insided/deprecated-control/ui-components/AlertComponent'
import { publish, ModulesContext } from '../../utils/globals'
import { CurrentUserContext } from '../../server-middleware/userContext'
import axios from 'axios'
import { ContentType, getConsistentContentType } from '@insided/shared/utils/contentType'
import classNames from 'classnames'
import {
  getInsidedData
} from '@insided/destination/utils/insidedDataStorage'
import { TranslationCategory } from '@insided/deprecated-libs/translationsUtils/translations'

export interface Alert {
  message: string | ComponentChild
  type: 'success' | 'error'
}

interface ActionsWithPublicIds {
  topicId: number | null
  postId: number
  privateId?: undefined
  privateReplyId?: undefined
  contentType: ContentType
  onEditClick?: undefined
}

interface ActionsWithReplyId {
  topicId?: undefined
  postId: number
  privateId?: undefined
  privateReplyId: number
  contentType: ContentType
  onEditClick: () => void
}

interface ActionsWithTopicId {
  topicId: number
  postId?: undefined
  privateId: number
  privateReplyId?: undefined
  contentType: ContentType
  onEditClick: () => void
}

interface NoLiking {
  isLiked?: undefined
  showLike: false
}

interface WithLiking {
  isLiked: boolean
  showLike: true
}

type Props = {
  onLikeClick?: () => void
  showEdit: boolean
  showReport: boolean
  showBestAnswer: boolean
  quoteText: string
  quoteUsername: string
  isSubscribed?: boolean
  topicTitle: string
} & (ActionsWithPublicIds | ActionsWithReplyId | ActionsWithTopicId) & (NoLiking | WithLiking)

interface State {
  isExtraMenuOpen: boolean
  isShareMenuOpen: boolean
  isLiked: boolean|undefined
  isBestAnswerClicked: boolean
  showReportContentModal: boolean
  alert?: Alert
}

class PostActions extends Component<Props, State> {
  likePending?: boolean
  shareTimeout?: NodeJS.Timeout

  constructor (props: Props) {
    super(props)
    this.state = {
      isExtraMenuOpen: false,
      isShareMenuOpen: false,
      isLiked: props.isLiked,
      isBestAnswerClicked: false,
      showReportContentModal: false,
      alert: undefined
    }
    this.onLikeClick = this.onLikeClick.bind(this)
    this.onQuoteClick = this.onQuoteClick.bind(this)
    this.onEditClick = this.onEditClick.bind(this)
    this.onShareEnter = this.onShareEnter.bind(this)
    this.onShareLeave = this.onShareLeave.bind(this)
    this.handleBestAnswerClick = this.handleBestAnswerClick.bind(this)
    this.showReportContentModal = this.showReportContentModal.bind(this)
    this.handleShowAlert = this.handleShowAlert.bind(this)
  }

  async componentDidMount() {
    const phrases = [
      { module: 'Forum', key: 'topic_view.reported_content_option.report' },
      { module: 'Forum', key: 'topic_view.reported_content_modal.report_content' },
      { module: 'Forum', key: 'topic_view.reported_content_modal.topic' },
      { module: 'Forum', key: 'topic_view.reported_content_modal.note' },
      { module: 'Forum', key: 'topic_view.reported_content_modal.reason' },
      { module: 'Forum', key: 'topic_view.reported_content_modal.reason.placeholder' },
      { module: 'Forum', key: 'topic_view.reported_content_modal.reason_not_provided.error' },
      { module: 'Forum', key: 'topic_view.reported_content_modal.error' },
      { module: 'Forum', key: 'topic_view.reported_content_modal.cancel' },
      { module: 'Forum', key: 'topic_view.reported_content.success' }
    ]
    await Promise.all(phrases.map(async p => await new Promise((resolve) => fetchPhrase(p.module as TranslationCategory, p.key, phrase => resolve(phrase)))))
  }

  showReportContentModal(show: boolean) {
    this.setState({ showReportContentModal: show })
  }

  handleShowAlert(type: 'success' | 'error', message?: string | ComponentChild) {
    this.setState({
      alert: {
        type: type,
        message: message ?? <Phrase p='topic_view.reported_content_modal.error' />
      }
    })
  }

  showLoginModal (e: Event) {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('GuestUserAction'))
  }

  updateLikeState (isLiked: boolean) {
    this.setState({ isLiked: isLiked })

    if (this.props.onLikeClick) {
      this.props.onLikeClick()
      return
    }
    // trigger legacy user like list to update
    window.app.events.commands.execute(`post:like:${this.props.postId}`, {
      currentUserHasLiked: isLiked
    })
  }

  async makeLikeRequest (newLikeState: boolean) {
    const { privateReplyId, contentType, privateId } = this.props
    if (privateReplyId && contentType) {
      return await axios.post<{likedBy: number}>(`/community/api/${getConsistentContentType(contentType)}s/replies/${privateReplyId}/${newLikeState ? 'like' : 'unlike'}`, { likedBy: getInsidedData('user').userid })
    }

    return await axios.post<{likedBy: number}>(`/community/api/${getConsistentContentType(contentType)}s/${privateId}/${newLikeState ? 'like' : 'unlike'}`, { likedBy: getInsidedData('user').userid })
  }

  async onLikeClick (e: Event) {
    e.preventDefault()
    if (this.likePending) return
    this.likePending = true
    const newIsLiked = !this.state.isLiked
    this.updateLikeState(newIsLiked)

    try {
      await this.makeLikeRequest(newIsLiked)
    } catch (e) {
      this.updateLikeState(!newIsLiked)
      throw e
    } finally {
      this.likePending = false
    }
  }

  onQuoteClick (e: Event) {
    e.preventDefault()
    publish('quote', {
      username: this.props.quoteUsername,
      content: this.props.quoteText
    })
  }

  onEditClick (e: Event) {
    if (!this.props.onEditClick) return
    e.preventDefault()
    this.props.onEditClick()
  }

  onShareEnter () {
    if (this.shareTimeout) clearTimeout(this.shareTimeout)
    this.setState({ isShareMenuOpen: true })
  }

  onShareLeave () {
    this.shareTimeout = setTimeout(() => this.setState({ isShareMenuOpen: false }), 300)
  }

  handleBestAnswerClick (e: Event) {
    if (this.state.isBestAnswerClicked) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    this.setState({
      isBestAnswerClicked: true
    })
  }

  render () {
    const {
      contentType,
      showBestAnswer,
      postId,
      showLike,
      showEdit,
      showReport,
      topicId,
      isSubscribed,
      privateReplyId,
      topicTitle,
      privateId
    } = this.props
    const { showReportContentModal, isLiked, isShareMenuOpen, alert } = this.state

    return (
      <ModulesContext.Consumer>
        {modules => (
          <CurrentUserContext.Consumer>
            {user => (
              <ul className={classNames('list', 'clearfix', 'text--meta', 'post__actions', 'qa-post-actions')}>
                {showBestAnswer
                  ? <li className='post__action-item' data-helper='mark-answer'>
                    <a href={`/topic/markanswer?postid=${postId}`} onClick={this.handleBestAnswerClick} className={classNames('icon', ' icon--b', 'icon--checkmark', 'link--quiet', 'post__action-link', 'js-mark-answer')}>
                      <span className='post__action-link__text'>
                        <Phrase p='Best answer' />
                      </span>
                    </a>
                  </li>
                  : null}

                {showLike
                  ? <li className='post__action-item'>
                    <a className={classNames('link', 'link--quiet', 'icon--b', 'icon--thumb-up', 'qa-like-link', 'post__action-link', isLiked ? 'is-active' : '')} href='#' onClick={user.id ? this.onLikeClick : this.showLoginModal}>
                      <span className='post__action-link__text'>
                        <Phrase m='Common' key={isLiked ? 'unlike' : 'like'} p={isLiked ? 'unlike' : 'like'} />
                      </span>
                    </a>
                  </li>
                  : null}

                <li className='post__action-item'>
                  <a className={classNames('link', 'link--quiet', 'icon--b', 'icon--quote', 'qa-icon-quote', 'post__action-link')} href='#' onClick={this.onQuoteClick}>
                    <span className='post__action-link__text'>
                      <Phrase p='Quote' />
                    </span>
                  </a>
                </li>

                {topicId
                  ? <li className='post__action-item'>
                    <TopicFavorite id={topicId} isSubscribed={isSubscribed} />
                  </li>
                  : null}

                {topicId && modules.includes('TopicSocialShare')
                  ? <li className='post__action-item'>
                    <div className='tooltip--share'>
                      <div className='tooltip tooltip--day' onMouseLeave={this.onShareLeave} onMouseEnter={this.onShareEnter}>

                        <span className={classNames('link', 'link--quiet', 'icon--b', 'icon--share', 'tooltip-trigger', 'post__action-link')}>
                          <span className='post__action-link__text'>
                            <Phrase p='Share' />
                          </span>
                        </span>
                        {isShareMenuOpen
                          ? <div className={classNames('tooltip__content', 'js-tooltip-content', 'position--bottom', 'is-enabled is-active')}>
                            <i className='arrow' style='left: 50%' />

                            <Phrase p='twitter.share.title'>
                              {twitterPhrase => (
                                <a title={twitterPhrase} className={classNames('btn', 'btn--icon', 'btn--twitter', 'qa-button-twitter')} href={`http://twitter.com/intent/tweet?text=https://${window.location.hostname + window.location.pathname}`} target='_blank' rel='noreferrer noopener'>
                                  <Phrase p='twitter.share' />
                                </a>
                              )}</Phrase>

                            <Phrase p='facebook.share.title'>
                              {facebookPhrase => (
                                <a title={facebookPhrase} className={classNames('btn', 'btn--icon', 'btn--facebook', 'qa-button-facebook')} href={`https://www.facebook.com/sharer/sharer.php?u=https://${window.location.hostname + window.location.pathname}`} target='_blank' rel='noreferrer noopener'>
                                  <Phrase p='facebook.share' />
                                </a>
                              )}</Phrase>

                            <Phrase p='linkedin.share.title'>
                              {linkedinPhrase => (
                                <a title={linkedinPhrase} className={classNames('btn', 'btn--icon', 'btn--linkedin', 'qa-button-linkedin')} href={`https://www.linkedin.com/shareArticle?mini=true&url=https://${window.location.hostname + window.location.pathname}`} target='_blank' rel='noreferrer noopener'>
                                  <Phrase p='linkedin.share' />
                                </a>
                              )}</Phrase>

                            <Phrase p='whatsapp.share.title'>
                              {whatsappPhrase => (
                                <a title={whatsappPhrase} className={classNames('btn btn--icon btn--whatsapp icon icon--whatsapp is-hidden-mobile-up')} href={`http://www.linkedin.com/shareArticle?mini=true&amp;url=https://${window.location.hostname + window.location.pathname}`} target='_blank' rel='noreferrer noopener'>
                                  <Phrase p='whatsapp.share' />
                                </a>
                              )}</Phrase>

                          </div>
                          : null}
                      </div>
                    </div>
                  </li>
                  : null}

                {user.id
                  ? <ExtraMenu
                      privateReplyId={privateReplyId}
                      contentType={contentType}
                      postId={postId}
                      showEdit={showEdit}
                      showReport={showReport}
                      onEditClick={this.onEditClick}
                      showReportContentModal={this.showReportContentModal}
                    />
                  : null}

                {showReportContentModal &&
                  <ReportContentModal
                    topicTitle={topicTitle}
                    topicId={privateId}
                    replyId={privateReplyId}
                    contentType={contentType}
                    showReportContentModal={this.showReportContentModal}
                    userId={user.id}
                    onShowAlert={this.handleShowAlert}
                  />}

                {alert?.message &&
                  <AlertComponent
                    sticky
                    messageType={alert.type}
                    isVisibleFor={3000}
                    onTimeoutEnd={() => this.setState({ alert: undefined })}
                  >
                    {alert.message}
                  </AlertComponent>}
              </ul>
            )}
          </CurrentUserContext.Consumer>
        )}
      </ModulesContext.Consumer>
    )
  }
}

interface ExtraMenuState {
  isExtraMenuOpen: boolean
}

interface ExtraMenuProps {
  postId?: number
  privateReplyId?: number
  contentType: ContentType
  showEdit: boolean
  showReport: boolean
  onEditClick: (e: Event) => void
  showReportContentModal: (show: boolean) => void
}

class ExtraMenu extends Component<ExtraMenuProps, ExtraMenuState> {
  extraTimeout?: NodeJS.Timeout

  constructor () {
    super()
    this.state = {
      isExtraMenuOpen: false
    }
    this.onExtraEnter = this.onExtraEnter.bind(this)
    this.onExtraLeave = this.onExtraLeave.bind(this)
    this.onReportClick = this.onReportClick.bind(this)
  }

  onExtraEnter () {
    if (this.extraTimeout) clearTimeout(this.extraTimeout)
    this.setState({ isExtraMenuOpen: true })
  }

  onExtraLeave () {
    this.extraTimeout = setTimeout(() => this.setState({ isExtraMenuOpen: false }), 300)
  }

  onReportClick (e: Event) {
    e.preventDefault()
    const { showReportContentModal } = this.props
    if (common.isModuleActive('reportedContent')) {
      showReportContentModal(true)
    } else {
      // trigger legacy report modal
      window.app.events.commands.execute('modal:report:content', `/post/report?postid=${this.props.postId}`)
    }
  }

  getPermalink () {
    const { postId, privateReplyId, contentType } = this.props
    const topicLink = `https://${window.location.hostname}${window.location.pathname}`
    if (!postId) return topicLink
    const misAlignedByIdContentTypes = ['idea', 'productUpdate']
    const id = misAlignedByIdContentTypes.includes(contentType) ? privateReplyId ?? postId : postId
    // Due to mis alignment of ids in old and new topic view pages privateReplyId should only be used for ideas and
    // product updates for getting reply link to stay consistent with old links
    return `${topicLink}?postid=${id}#post${id}`
  }

  render () {
    const { showEdit, postId, onEditClick, showReport } = this.props
    const { isExtraMenuOpen } = this.state

    return (
      <li className={classNames('tooltip', 'tooltip--day', 'post__action-item', 'post__action-item--more')} onMouseEnter={this.onExtraEnter} onMouseLeave={this.onExtraLeave}>
        <a className={classNames('post__action-link', 'tooltip-trigger', 'js-tooltip-trigger')}>
          <span className={classNames('icon--auto-width', 'icon--ellipsis', 'qa-topic-post-ellipsis')} />
        </a>
        {isExtraMenuOpen
          ? <ul className={classNames('list', 'tooltip__content', 'tooltip__content--more position--bottom-right', 'is-enabled', 'is-active')}>
            <i className='arrow' />
            {showEdit
              ? <li className='more__item post__action-dropdown-item'>
                <a className={classNames('link', 'js-edit', 'qa-topic-post-edit', 'post__action-dropdown-link')} href={`/post/edit?postid=${postId}`} data-postid={postId} onClick={onEditClick}>
                  <Phrase p='Edit' />
                </a>
              </li>
              : null}

            <li className='more__item post__action-dropdown-item'>
              <a className={classNames('link', 'qa-topic-post-permalink', 'post__action-dropdown-link')} href={this.getPermalink()}>
                <Phrase p='Permalink' />
              </a>
            </li>

            {showReport
              ? <li className='more__item post__action-dropdown-item'>
                <a className={classNames('link', 'qa-topic-post-report', 'post__action-dropdown-link')} onClick={this.onReportClick}>
                  <Phrase
                    p={common.isModuleActive('reportedContent') ? 'topic_view.reported_content_option.report' : 'Flag'}
                  />
                </a>
              </li>
              : null}

          </ul>
          : null}
      </li>
    )
  }
}

export default PostActions
